// src/views/dashboard/events/EventDetail.jsx
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Label } from "@oc-labs/wt-storybook";
import { Calendar, Users, MapPin, NotepadText, ArrowRight } from "lucide-react";
import useCompany from "../../../hooks/useCompany";
import useEvent from "../../../hooks/useEvent";
import useRwas from "../../../hooks/useRwas";
import UpsertEventModal from "../../../components/modals/upsert-event-v2/UpsertEventModal";
import useModal from "../../../hooks/useModal";
import DeleteModal from "../../../components/modals/DeleteModal";
import AttendeesList from "./AttendeesList";
import MembersDrawer from "./components/MembersDrawer";
import EventGallery from "../../../components/modals/EventGallery";
import { useCreateBlockNote } from "@blocknote/react";
import "@blocknote/core/fonts/inter.css";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";

function formatEuropeanDateTime(dateString) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function ProgramDisplay({ content }) {
  if (!content) {
    return <p className="text-sm font-semibold">—</p>;
  }
  // Parse content safely
  let parsedContent;
  try {
    if (content && typeof content === 'string') {
      parsedContent = JSON.parse(content);
    }
  } catch (e) {
    // If parsing fails, treat it as plain text
    if (content) {
      parsedContent = [
        {
          type: "paragraph",
          content: content,
        },
      ];
    }
  }

  if (
    !parsedContent ||
    (Array.isArray(parsedContent) &&
      parsedContent.every(
        (block) => !block?.content || block.content.length === 0
      ))
  ) {
    return <p className="text-sm font-semibold">—</p>;
  }

  const editor = useCreateBlockNote({
    initialContent: parsedContent,
    editable: false,
  });

  return (
    <div className="program-display-custom">
      <style>{`
        .program-display-custom .bn-container,
        .program-display-custom .bn-container .bn-editor,
        .program-display-custom .bn-block-group {
          background: transparent !important;
        }
        .program-display-custom .bn-container .bn-editor {
          font-size: 0.875rem;
          line-height: 1.5;
          color: #111827;
          padding: 0;
          margin: 0;
        }
        .program-display-custom .bn-container .bn-editor .bn-block-outer {
          margin: 0;
        }
        .program-display-custom .bn-container .bn-editor .bn-block-content {
          margin: 0;
          padding: 0;
        }
        .program-display-custom .bn-container .bn-editor p,
        .program-display-custom .bn-container .bn-editor h1,
        .program-display-custom .bn-container .bn-editor h2,
        .program-display-custom .bn-container .bn-editor h3,
        .program-display-custom .bn-container .bn-editor li {
          font-family: inherit;
          margin: 0;
          padding: 0;
        }
        .program-display-custom .bn-container .bn-editor p {
          font-size: 0.875rem;
          font-weight: 600;
        }
      `}</style>
      <BlockNoteView editor={editor} editable={false} theme="light" />
    </div>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const { activeCompany } = useCompany();
  const navigate = useNavigate();
  const [showAttendees, setShowAttendees] = useState(false);

  const { open } = useModal("upsert-event");
  const { open: openDeleteModal } = useModal("delete-modal");
  const { open: openMembersDrawer } = useModal('members-drawer');
  const { deleteEvent, loadById, getEventAttendees } = useEvent();
  const { getAllRWAs } = useRwas();
  const [err, setErr] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const [attendees, setAttendees] = useState([]);
  const [assetLabels, setAssetLabels] = useState({});

  const loadAttendees = async () => {
    if (!event?.id) return;
    const attd = await getEventAttendees(event.id);
    console.log("Loaded attendees:", attd);
    setAttendees(attd);
  }

  const loadEvent = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const evt = await loadById(id);
      setEvent(evt);
      setErr(null);
    } catch (e) {
      setErr("Could not load the event. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadEvent();
  }, [id]);
  
  useEffect(() => {
    loadAttendees();
  }, [event]);


  useEffect(() => {
    setAssetLabels({});
    if (!activeCompany?.id) return;
    getAllRWAs(activeCompany.id).then((rwas) => {
      const map = {};
      (Array.isArray(rwas) ? rwas : []).forEach((r) => {
        const id = String(r?.id ?? r?.rwaId ?? "");
        const name = String(r?.name ?? r?.title ?? id).trim();
        const type = String(r?.type ?? r?.category ?? "").trim();
        if (id) map[id] = `${name}${type ? ` — ${type}` : ""}`;
      });
      setAssetLabels(map);
    }).catch(() => {});
  }, [activeCompany?.id]);

  const handleEdit = () => {
    if (!event) return;
    open({
      ...event,
      onSaved: async (saved) => {
        await loadEvent();
        setEvent(saved);
      },
    });
  };

  const requestDelete = () => {
    if (!event) return;
    const name = event.name ?? "this event";
    openDeleteModal({
      title: "Delete Event",
      text: `Are you sure you want to delete "${name}"?`,
      onDelete: async () => {
        try {
          const eventId = event.id;
          await deleteEvent(eventId);
          navigate("/dashboard/events");
        } catch {
          alert("Could not delete the event. Please try again.");
        }
      },
    });
  };

  const isEventPassed = event?.endDate && new Date(event?.endDate) < new Date();
  const isEventOngoing = event?.startDate && event?.endDate && new Date(event?.startDate) <= new Date() && new Date(event?.endDate) >= new Date();

  if(err) return <div className="p-6 text-red-600">{err}</div>;
  if(loading) return <div className="p-6 italic text-gray-500">Loading event...</div>;
  if (showAttendees) return <AttendeesList hideAttendeesList={() => setShowAttendees(false)} event={event}  attendees={attendees} />;


  const requiredAssetsText =
    Array.isArray(event?.contractRequiredToAttend) &&
      event.contractRequiredToAttend.length
      ? event.contractRequiredToAttend
        .map((id) => assetLabels[id] || "")
        .join(", ")
      : "—";

  const requiredAssetsToDiscountText =
    Array.isArray(event?.contractRequiredToDiscount) &&
      event.contractRequiredToDiscount.length
      ? event.contractRequiredToDiscount
        .map((id) => assetLabels[id] || "")
        .join(", ")
      : "—";

  const priceWithCurrency =
    event?.price > 0 && event?.currency
      ? `${event.price} ${event.currency}`
      : "—";


  return (
    <div className="p-6 w-full mx-auto">
      <UpsertEventModal reload={loadEvent} />
      <DeleteModal />
      <MembersDrawer event={event} attendees={attendees} reloadAttendees={loadAttendees} reloadEvent={loadEvent} />

      <div className="flex items-center justify-between mb-4 mx-2">
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            size="small"
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            type="danger"
            size="small"
            onClick={requestDelete}
          >
            Delete
          </Button>
        </div>
        <div>
          <Button
            type="secondary"
            size="small"
            onClick={() => navigate("/dashboard/events")}
          >
            Back
          </Button>
        </div>
      </div>

      <div className="relative rounded-3xl overflow-hidden">
        <img
          src={event.coverImage || "https://www.nevasport.com/fotos/291021/923435-Coleccion-Zai-20212022-Coleccion-Zai-Skis-20212022.jpg"}
          alt={event.name || "Event Cover"}
          className="w-full h-96 object-cover"
        />
        <div className="absolute top-4 left-4 flex gap-2">
          <Label
            prefix={
              <span
                className={`
                  inline-block w-3 h-3 rounded-full 
                  ${isEventPassed ? "bg-red-500" : isEventOngoing ? "bg-green-500" : "bg-blue-500"}
                `}
                aria-hidden="true"
              />
            }
          >
            {isEventPassed ? "PAST" : isEventOngoing ? "ONGOING" : "UPCOMING"}
          </Label>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src={`https://api.dicebear.com/9.x/initials/svg?backgroundColor=2B36D9&seed=${encodeURIComponent(
              activeCompany?.name || "Organizer Name"
            )}`}
            alt={activeCompany?.name || "Organizer Name"}
            className="h-10 w-10 rounded-full object-cover"
          />
          <div>
            <div className="font-semibold text-gray-900">
              {activeCompany?.name || "Organizer Name"}
            </div>
            <div className="text-sm text-gray-600">Organizer</div>
          </div>
        </div>

        <div>
          <Button
            data-members-toggle
            type="primary"
            icon={
              <img
                src="/icons/user-white.svg"
                alt=""
                width={16}
                height={16}
                className="block w-5 h-5 shrink-0"
              />
            }
            className="whitespace-nowrap"
            onClick={openMembersDrawer}
          >
            Invite members
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {/* START DATE / ATTENDEES */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded-2xl px-4 py-3">
            <div className="text-xs text-gray-900 flex items-center gap-2">
              <Calendar size={16} /> START DATE
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900">{formatEuropeanDateTime(event.startDate)}
            </div>
          </div>

          <div
            className="bg-gray-100 rounded-2xl px-4 py-3 cursor-pointer"
            onClick={() => setShowAttendees(true)}
          >
            <div className="text-xs text-gray-900 flex items-center gap-2">
              <Users size={16} /> ATTENDEES
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {event.totalAttendees} / {event.maxAttendees || "∞"}
              <ArrowRight size={16} className="inline-block ml-2 text-gray-500" />
            </div>
          </div>
        </div>

        {/* END DATE / LOCATION */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded-2xl px-4 py-3">
            <div className="text-xs text-gray-900 flex items-center gap-2">
              <Calendar size={16} /> END DATE
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {formatEuropeanDateTime(event.endDate)}
            </div>
          </div>

          <div className="bg-gray-100 rounded-2xl px-4 py-3">
            <div className="text-xs text-gray-900 flex items-center gap-2">
              <MapPin size={16} /> LOCATION
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {event.location || "—"}
            </div>
          </div>
        </div>

        {/* TITLE */}
        <div className="bg-gray-100 rounded-2xl px-4 py-3">
          <div className="text-xs text-gray-900 flex items-center gap-2">
            <NotepadText size={16} /> TITLE
          </div>
          <h2 className="mt-1 text-sm font-semibold">
            {event.name || "—"}
          </h2>
        </div>

        {/* DESCRIPTION */}
        <div className="bg-gray-100 rounded-2xl px-4 py-3">
          <div className="text-xs text-gray-900 flex items-center gap-2 mb-2">
            <NotepadText size={16} /> DESCRIPTION
          </div>
          <ProgramDisplay content={event.description} />
        </div>

        {/* PROGRAM */}
        <div className="bg-gray-100 rounded-2xl px-4 py-3">
          <div className="text-xs text-gray-900 flex items-center gap-2 mb-2">
            <NotepadText size={16} /> PROGRAM
          </div>
          <ProgramDisplay content={event.program} />
        </div>

        {/* PRICE / DISCOUNT PRICE */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded-2xl px-4 py-3">
            <div className="text-xs text-gray-900 flex items-center gap-2">
              <NotepadText size={16} /> PRICE
            </div>
            <h2 className="mt-1 text-sm font-semibold">{priceWithCurrency}</h2>
          </div>

          <div className="bg-gray-100 rounded-2xl px-4 py-3">
            <div className="text-xs text-gray-900 flex items-center gap-2">
              <NotepadText size={16} /> DISCOUNT PRICE
            </div>
            <h2 className="mt-1 text-sm font-semibold">
              {event.discountPrice || "—"}
            </h2>
          </div>
        </div>

        {/* ASSET REQUIRED TO ATTEND / ASSET REQUIRED TO GET DISCOUNT */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded-2xl px-4 py-3">
            <div className="text-xs text-gray-900 flex items-center gap-2">
              <NotepadText size={16} /> ASSET REQUIRED TO ATTEND
            </div>
            <h2 className="mt-1 text-sm font-semibold">{requiredAssetsText}</h2>
          </div>

          <div className="bg-gray-100 rounded-2xl px-4 py-3">
            <div className="text-xs text-gray-900 flex items-center gap-2">
              <NotepadText size={16} /> ASSET REQUIRED TO GET DISCOUNT
            </div>
            <h2 className="mt-1 text-sm font-semibold">
              {requiredAssetsToDiscountText}
            </h2>
          </div>
        </div>

        {/* Gallery */}
        <EventGallery images={event.galleryImages} />
      </div>
    </div>
  );
}